import React, { useState, useEffect, useRef, useMemo } from 'react';
import { holeToken } from '../../lib/sitzung';
import { API_BASE, apiHeaders, apiPost, zeigeApiFehler } from '../../lib/api';
import { ladeFehlzeitenGrenzen } from '../../lib/utils';
import { useIsMobile, useEscapeKey, useEindeutigeKlassen, useOhneMikroKnopf } from '../../lib/hooks';
import { Schueler, KlasseFachEintrag } from '../../types';
import { useFab } from '../../contexts/FabContext';
import { useSchueler } from '../../contexts/SchuelerContext';
import { toast } from '../../lib/toast';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdFeld, LdFeldReihe, LdAuswahl, LdDatum, LdZahl, LdUmschalter, LdKiHinweis, LdFensterFuss } from '../../components/ld/LdFenster';
import { AttestFrage } from './AttestFrage';
import './foto-scan.css';
import { KarussellHeader } from '../../components/patterns/KarussellHeader';
import { KlassenKopf } from '../klasse/KlassenKopf';
import { useAufmerksamkeit, fehltText } from '../klasse/aufmerksamkeit';
import { SchuelerProfil } from '../schueler';
import { MIcon } from '../../lib/micon';
import { FehlzeitenVorjahreUebersicht } from './FehlzeitenVorjahre';
import { schuljahrFenster } from '../../lib/schuljahr';
import { mikrofonAnfragen, zeigeMikrofonFehler } from '../../lib/mikrofon';
import { nimmFehlzeitenKlasse } from '../../lib/nav';

interface KrankmeldungErgebnis {
  schueler: string | null;
  datum: string | null;
  grund: string | null;
  arzt: boolean | null;
  tage: number | null;
}

type SprechPhase = 'idle' | 'aufnahme' | 'transkribiert' | 'analysiert' | 'fehler';

// ── Foto-Scan Modal ─────────────────────────────────────────────────────────
function FotoScanModal({
  onClose,
  onEintragen,
  attestpflicht,
}: {
  onClose: () => void;
  /** Welle B4: hat das erkannte Kind eine aktive Attestpflicht? */
  attestpflicht?: (name: string) => boolean;
  onEintragen: (name: string, datum: string, stunden: number, tage: number, entschuldigt: boolean, attest?: boolean) => void;
}) {
  useEscapeKey(onClose);
  const [phase, setPhase] = useState<'upload' | 'laden' | 'vorschau' | 'erfolg'>('upload');
  const [fehler, setFehler] = useState('');
  const [erkannt, setErkannt] = useState<KrankmeldungErgebnis | null>(null);
  const [stunden, setStunden] = useState(6);
  const [tage,    setTage]    = useState(1);
  const [entschuldigt, setEntschuldigt] = useState(true);
  const [attest, setAttest] = useState(false);   // Welle B4: Vorgabe „Noch nicht“
  const kameraInputRef = useRef<HTMLInputElement>(null);
  const galerieInputRef = useRef<HTMLInputElement>(null);

  async function handleDatei(datei: File) {
    setPhase('laden');
    setFehler('');
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = reject;
        reader.readAsDataURL(datei);
      });
      const data = await apiPost<KrankmeldungErgebnis>('/api/krankmeldung', { bild: base64 });
      setErkannt(data);
      setPhase('vorschau');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Unbekannter Fehler';
      setFehler(msg);
      setPhase('upload');
    }
  }

  function formatDatum(d: string) {
    if (!d) return '–';
    const parts = d.split('-');
    if (parts.length !== 3) return d;
    return `${parts[2]}.${parts[1]}.${parts[0]}`;
  }

  function handleBestätigen() {
    if (!erkannt) return;
    onEintragen(erkannt.schueler ?? '', erkannt.datum ?? '', stunden, tage, entschuldigt || attest, attestpflicht?.(erkannt.schueler ?? '') ? attest : false);
    setPhase('erfolg');
  }

  // Welle Z4: Fenster im K4-Rahmen (Kopf, ld-Felder, Fußleiste) — Ablauf und Speichern unverändert
  const mitPflicht = !!erkannt && !!attestpflicht?.(erkannt.schueler ?? '');
  const dateiwahl = (
    <>
      {/* Kamera-Input (capture="environment" → Rückkamera auf iOS/Android) */}
      <input ref={kameraInputRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleDatei(f); }} />
      {/* Galerie-Input (kein capture → Galerie-Picker) */}
      <input ref={galerieInputRef} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) handleDatei(f); }} />
    </>
  );
  const kopf = phase === 'vorschau' ? { titel: 'Erkannte Daten prüfen', kontext: 'Aus dem Foto der Krankmeldung' }
    : phase === 'erfolg' ? { titel: 'Fehlzeit eingetragen' }
    : { titel: 'Krankmeldung scannen', kontext: 'Name, Datum und Grund werden erkannt' };
  const fuss = phase === 'upload' ? (<>
      <span className="ld-fe-fuss__links ld-fe-fuss__links--zeile">
        <button type="button" className="ld-kl-link" onClick={() => galerieInputRef.current?.click()}>Aus Galerie wählen</button>
      </span>
      <LdFensterFuss onAbbrechen={onClose} haupt={<><MIcon name="photo_camera" size={18} />Foto aufnehmen</>} onHaupt={() => kameraInputRef.current?.click()} />
    </>)
    : phase === 'vorschau' ? (<>
      <span className="ld-fe-fuss__links ld-fe-fuss__links--zeile">
        <button type="button" className="ld-kl-link" onClick={() => { setPhase('upload'); setFehler(''); }}>Neues Foto</button>
      </span>
      <LdFensterFuss onAbbrechen={onClose} haupt="Fehlzeit eintragen" onHaupt={handleBestätigen} />
    </>)
    : phase === 'erfolg' ? <button type="button" className="ld-fe-knopf ld-fe-knopf--haupt" onClick={onClose}>Fertig</button>
    : undefined;

  return (
    <LdSheet open={true} onClose={onClose} maxWidth={460} kopf={kopf} fuss={fuss}>
      {dateiwahl}
      {phase === 'upload' && (<>
        <p className="ld-fe-text">Foto der Krankmeldung aufnehmen oder aus der Galerie wählen.</p>
        {fehler && <p className="ld-fe-fehler" role="alert">{fehler}</p>}
      </>)}

      {phase === 'laden' && (
        <div className="ld-fz-foto__laedt" role="status">
          <span className="ld-fz-foto__kreis" aria-hidden="true" />
          <span>KI liest die Krankmeldung …</span>
        </div>
      )}

      {phase === 'vorschau' && erkannt && (<>
        <LdKiHinweis>Aus dem Foto erkannt. Bitte kurz prüfen.</LdKiHinweis>
        <dl className="ld-fz-foto__daten">
          <dt>Schüler/in</dt><dd>{erkannt.schueler || '–'}</dd>
          <dt>Datum</dt><dd>{formatDatum(erkannt.datum ?? '')}</dd>
          <dt>Grund</dt><dd>{erkannt.grund || '–'}</dd>
          <dt>Attest auf dem Foto</dt><dd>{erkannt.arzt ? 'liegt vor' : 'nicht vorhanden'}</dd>
        </dl>
        <LdFeldReihe>
          <LdFeld label="Fehlstunden"><LdZahl aria-label="Fehlstunden" value={stunden} min={0} max={40} onChange={setStunden} /></LdFeld>
          <LdFeld label="Fehltage"><LdZahl aria-label="Fehltage" value={tage} min={0} max={30} onChange={setTage} /></LdFeld>
        </LdFeldReihe>
        <LdFeld label="Status">
          <LdUmschalter aria-label="Status" wert={entschuldigt ? 'ja' : 'nein'} onChange={w => setEntschuldigt(w === 'ja')}
            optionen={[{ wert: 'nein', label: 'Unentschuldigt', ton: 'amber' }, { wert: 'ja', label: 'Entschuldigt', ton: 'gruen' }]} />
        </LdFeld>
        {mitPflicht && <AttestFrage attest={attest} onChange={setAttest} />}
      </>)}

      {phase === 'erfolg' && erkannt && (
        <div className="ld-z3-feld">
          <b>{erkannt.schueler}</b>
          <p className="ld-fe-text">{formatDatum(erkannt.datum ?? '')} · {erkannt.grund ?? '–'}</p>
          <p className="ld-fe-text">{stunden} Std., {tage} Tage, {entschuldigt ? 'entschuldigt' : 'unentschuldigt'}{erkannt.arzt ? ', Attest liegt vor' : ''}</p>
        </div>
      )}
    </LdSheet>
  );
}

// ── Fehlzeiten-Screen ─────────────────────────────────────────────────────
// Welle U8: vorgabeKlasseId gesetzt = eingebettet im Klassen-Screen (KlasseTab) — die
// Klassenwahl oben gilt, die eigene Klassen-Leiste entfällt. Ohne (Seitenleiste) wie bisher.
// Welle K1: ein Hauptknopf („Fehlzeit eintragen“, Foto als zweiter), eine Überblickszeile statt
// bunter Kacheln, Gruppen (§68, Achtung, Entschuldigung fehlt, unbedenklich eingeklappt), PDF unten.
// Eigenständig (Seitenleiste) mit dem Klassenkopf statt der Klassen-Chips. Das Fehlzeit-Diktat
// startet der schwebende Mikro-Knopf (fabHandler); onDetail meldet ein offenes Profil.
export function FehlzeitenScreen({ vorgabeKlasseId, onDetail }: { vorgabeKlasseId?: number | null; onDetail?: (offen: boolean) => void } = {}) {
  const eingebettet = vorgabeKlasseId !== undefined;
  const { schuelerListe, patchSchueler } = useSchueler();
  const isMobile = useIsMobile();
  const [ausgewaehlt, setAusgewaehlt] = useState<Schueler | null>(null);
  const [scanModal, setScanModal] = useState(false);
  const [manuellModal, setManuellModal] = useState(false);
  const [aktiveKlasseLokal, setAktiveKlasseLokal] = useState<KlasseFachEintrag | null>(null);
  const [exportKlasseId, setExportKlasseId] = useState<number | null>(null);
  const [exportLaden, setExportLaden] = useState(false);
  const [karussellSuche, setKarussellSuche] = useState('');
  const [okOffen, setOkOffen] = useState(false);

  const eindeutigeKlassen = useEindeutigeKlassen();

  // FAB-Handler: öffnet Fehlzeit-Diktat-Modal
  const { setFabHandler } = useFab();
  const fehlzeitFabStreamRef = React.useRef<Promise<MediaStream> | null>(null);
  useEffect(() => {
    setFabHandler(() => {
      // Pre-acquire stream in gesture context (iOS Safari: getUserMedia only works here)
      fehlzeitFabStreamRef.current = mikrofonAnfragen();
      setManuellModal(true);
    });
    return () => { setFabHandler(null); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Welle K1: Profil offen → Klassenkopf aus; am Handy liegt dann die feste Leiste des Profils unten
  useEffect(() => { onDetail?.(!!ausgewaehlt); }, [ausgewaehlt]); // eslint-disable-line react-hooks/exhaustive-deps
  useOhneMikroKnopf(isMobile && !!ausgewaehlt);

  // Aktive Klasse initialisieren sobald Klassen geladen
  useEffect(() => {
    if (eingebettet) return;
    if (!aktiveKlasseLokal && eindeutigeKlassen.length > 0) {
      setAktiveKlasseLokal(eindeutigeKlassen[0]);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eindeutigeKlassen]);

  // Welle B1: Klasse von außen (Push „Überfällige Entschuldigungen“) beim Einhängen abholen;
  // gesetzt wird sie, sobald die Klassenliste sie enthält (Muster KlasseTab)
  const [vorgabeVonAussen, setVorgabeVonAussen] = useState<number | null>(null);
  useEffect(() => {
    const k = nimmFehlzeitenKlasse();
    if (k != null && !eingebettet) setVorgabeVonAussen(k);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (vorgabeVonAussen == null) return;
    const k = eindeutigeKlassen.find(x => Number(x.klasse_id) === Number(vorgabeVonAussen));
    if (k) setAktiveKlasseLokal(k);
  }, [vorgabeVonAussen, eindeutigeKlassen]);

  // Welle U8: eingebettet folgt die Klasse der Auswahl oben; offene Fenster und ein
  // geöffnetes Schülerprofil werden beim Wechsel geschlossen (keine Mischung der Klassen)
  useEffect(() => {
    if (!eingebettet) return;
    const k = eindeutigeKlassen.find(x => Number(x.klasse_id) === Number(vorgabeKlasseId)) ?? null;
    setAktiveKlasseLokal(prev => (Number(prev?.klasse_id) === Number(k?.klasse_id) ? prev : k));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eingebettet, vorgabeKlasseId, eindeutigeKlassen]);
  const vorigeVorgabe = React.useRef(vorgabeKlasseId);
  useEffect(() => {
    if (vorigeVorgabe.current === vorgabeKlasseId) return;
    vorigeVorgabe.current = vorgabeKlasseId;
    setManuellModal(false); setScanModal(false); setAusgewaehlt(null); setKarussellSuche('');
  }, [vorgabeKlasseId]);

  // exportKlasseId an aktive Klasse koppeln
  useEffect(() => {
    if (aktiveKlasseLokal) setExportKlasseId(aktiveKlasseLokal.klasse_id);
  }, [aktiveKlasseLokal]);

  // Welle K1: Entschuldigung/Attest fehlt je Tag (Server, Regeln wie „Steht an“)
  const { daten: aufmerksamkeit } = useAufmerksamkeit(aktiveKlasseLokal?.klasse_id ?? null);

  function exportFehlzeiten() {
    if (!exportKlasseId) return;
    setExportLaden(true);
    fetch(`${API_BASE}/api/export/fehlzeiten/${exportKlasseId}`, { headers: apiHeaders() })
      .then(r => r.ok ? r.blob() : Promise.reject('Fehler'))
      .then(blob => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `Fehlzeiten_Klasse.pdf`; a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => toast('Export fehlgeschlagen', 'error'))
      .finally(() => setExportLaden(false));
  }

  function handleEintragen(name: string, _datum: string, stunden: number, tage: number, entschuldigt: boolean, attest = false) {
    const s = schuelerListe.find(s => s.name === name);
    if (s) {
      patchSchueler(s.id, prev => ({
        ...prev,
        fehlzeiten: {
          stunden_gesamt:         prev.fehlzeiten.stunden_gesamt         + stunden,
          stunden_unentschuldigt: prev.fehlzeiten.stunden_unentschuldigt + (entschuldigt ? 0 : stunden),
          tage_gesamt:            prev.fehlzeiten.tage_gesamt            + tage,
          tage_unentschuldigt:    prev.fehlzeiten.tage_unentschuldigt    + (entschuldigt ? 0 : tage),
        },
      }));
      apiPost<any>('/api/fehlzeiten', {
        schueler_id:  s.id,
        datum:        new Date().toISOString().split('T')[0],
        stunden,
        tage,
        entschuldigt,
        attest,   // Welle B4: Antwort auf „Liegt ein Attest vor?“
      }).then(data => {
        if (s.attestpflicht === 1) {
          toast(attest ? 'Fehlzeit mit Attest eingetragen' : 'Fehlzeit eingetragen, Attest steht noch aus', attest ? 'success' : 'info');
        } else if (!entschuldigt && data?.vorschlaege_erstellt > 0) {
          toast('Fehlzeit eingetragen, Note-6-Vorschlag erstellt');
        } else {
          toast('Fehlzeit eingetragen');
        }
      }).catch(err => zeigeApiFehler(err, 'Fehlzeit speichern fehlgeschlagen'));
    }
    setScanModal(false);
  }

  // §68 HSchG Grenzwerte (Schwellen der Lehrkraft, beim Start gecacht)
  const _fGrenzen = ladeFehlzeitenGrenzen();
  const fehlStatus = (f: number): 'kritisch' | 'achtung' | 'ok' => {
    if (f >= _fGrenzen.kritisch) return 'kritisch';
    if (f >= _fGrenzen.achtung)  return 'achtung';
    return 'ok';
  };

  // Nur Schüler der aktiven Klasse anzeigen
  const gefilterteSchueler = useMemo(() => {
    if (!aktiveKlasseLokal) return schuelerListe;
    return schuelerListe.filter(s => Number(s.klasse_id) === Number(aktiveKlasseLokal.klasse_id));
  }, [schuelerListe, aktiveKlasseLokal]);

  // Fehlzeiten direkt aus Schüler-State (patchSchueler hält ihn aktuell).
  // Welle U2: Status/Farbe nach unentschuldigten Stunden (gleiche Regel wie Heute/Radar).
  const effFehl = (s: Schueler) => s.fehlzeiten.stunden_unentschuldigt;
  const proKind = new Map((aufmerksamkeit?.schueler ?? []).map(k => [k.schueler_id, k]));
  const fehltTage = (s: Schueler) => { const k = proKind.get(s.id); return k ? k.ohne_entschuldigung.length + k.attest_offen.length : 0; };

  const sortiert = [...gefilterteSchueler].sort((a, b) => effFehl(b) - effFehl(a));
  const kritischListe = sortiert.filter(s => fehlStatus(effFehl(s)) === 'kritisch');
  const achtungListe  = sortiert.filter(s => fehlStatus(effFehl(s)) === 'achtung');
  // Jedes Kind steht nur in seiner wichtigsten Gruppe
  const fehltListe    = sortiert.filter(s => fehlStatus(effFehl(s)) === 'ok' && fehltTage(s) > 0);
  const okListe       = sortiert.filter(s => fehlStatus(effFehl(s)) === 'ok' && fehltTage(s) === 0)
    .sort((a, b) => a.name.localeCompare(b.name, 'de'));
  const reihenfolge = [...kritischListe, ...achtungListe, ...fehltListe, ...okListe];

  const zeile = (s: Schueler, unter: React.ReactNode, balken?: { anteil: number; klasse: string }) => (
    <button key={s.id} type="button" className="ld-kl-zeile" onClick={() => setAusgewaehlt(s)}>
      <span className="ld-kl-avatar" aria-hidden="true">{s.avatar}</span>
      <span className="ld-kl-zeile__text">
        <span className="ld-kl-zeile__name" style={{ display: 'block' }}>{s.name}</span>
        <span className="ld-kl-zeile__unter" style={{ display: 'block' }}>{unter}</span>
        {balken && <span className="ld-kf-schwelle" style={{ display: 'block' }}><span className={balken.klasse} style={{ width: `${Math.min(100, balken.anteil * 100)}%` }} /></span>}
      </span>
      <MIcon name="chevron_right" size={20} className="ld-kl-pfeil" />
    </button>
  );
  const stdText = (n: number) => String(n).replace('.', ',');
  const gruppe = (titel: string, liste: Schueler[], inhalt: (s: Schueler) => React.ReactNode) => liste.length > 0 && (
    <>
      <h2 className="ld-kl-abschnitt">{titel} <span className="ld-kl-abschnitt__zahl">{liste.length}</span></h2>
      <div className="ld-kl-karte">{liste.map(inhalt)}</div>
    </>
  );

  const karussellListe = reihenfolge.filter(s =>
    !karussellSuche || s.name.toLowerCase().includes(karussellSuche.toLowerCase())
  );
  const fzAktiverIndex = karussellListe.findIndex(s => s.id === ausgewaehlt?.id);

  if (ausgewaehlt) {
    const aktuell = schuelerListe.find(s => s.id === ausgewaehlt.id) ?? ausgewaehlt;
    return (
      <div className="ld-detail-vollbreite">
        <KarussellHeader
          onZurueck={() => { setAusgewaehlt(null); setKarussellSuche(''); }}
          onVorher={() => fzAktiverIndex > 0 && setAusgewaehlt(karussellListe[fzAktiverIndex - 1])}
          onNaechster={() => fzAktiverIndex < karussellListe.length - 1 && setAusgewaehlt(karussellListe[fzAktiverIndex + 1])}
          suchterm={karussellSuche}
          setSuchterm={setKarussellSuche}
          vorherDeaktiviert={fzAktiverIndex <= 0}
          naechsterDeaktiviert={fzAktiverIndex < 0 || fzAktiverIndex >= karussellListe.length - 1}
          zurueckLabel="Zu den Fehlzeiten"
        />
        <SchuelerProfil schueler={aktuell} startZiel="fehlzeiten" onBack={() => { setAusgewaehlt(null); setKarussellSuche(''); }} />
      </div>
    );
  }

  const n = { krit: kritischListe.length, acht: achtungListe.length, fehlt: fehltListe.length, ok: okListe.length };
  const gesamt = Math.max(1, n.krit + n.acht + n.fehlt + n.ok);

  return (
    <div className="ld-page-enter">
    <div>
      {/* Eigenständig (Seitenleiste): Klassenkopf statt Klassen-Chips */}
      {!eingebettet && (
        <KlassenKopf kompakt bereich="Fehlzeiten" klassen={eindeutigeKlassen} aktiveKlasse={aktiveKlasseLokal}
          onWechsel={k => { setAktiveKlasseLokal(k); setVorgabeVonAussen(null); }} />
      )}

      {/* Ein Hauptknopf, Foto als zweiter */}
      <div className="ld-kf-knoepfe">
        <button type="button" className="ld-kl-haupt" onClick={() => setManuellModal(true)}><MIcon name="add" size={19} />Fehlzeit eintragen</button>
        <button type="button" className="ld-kl-zweit" onClick={() => setScanModal(true)}><MIcon name="photo_camera" size={18} />Foto</button>
      </div>

      {gefilterteSchueler.length === 0 ? (
        <div className="ld-kl-karte ld-kl-leer">Noch keine Schüler angelegt. Richte zuerst die Klasse ein.</div>
      ) : (
        <>
          {/* Überblick: eine Zeile, gestapelter Balken */}
          <div className="ld-kl-karte ld-kf-blick">
            <div className="ld-kf-balken" role="img" aria-label={`${n.krit} über §68, ${n.acht} Achtung, ${n.fehlt} Entschuldigung fehlt, ${n.ok} unbedenklich`}>
              {n.krit > 0 && <span className="ld-kf-rot" style={{ flex: n.krit / gesamt }} />}
              {n.acht > 0 && <span className="ld-kf-amber-hell" style={{ flex: n.acht / gesamt }} />}
              {n.fehlt > 0 && <span className="ld-kf-amber" style={{ flex: n.fehlt / gesamt }} />}
              {n.ok > 0 && <span className="ld-kf-ruhig" style={{ flex: n.ok / gesamt }} />}
            </div>
            <div className="ld-kf-legende">
              <span><i className="ld-kf-rot" /><b>{n.krit}</b> über §68</span>
              {n.acht > 0 && <span><i className="ld-kf-amber-hell" /><b>{n.acht}</b> Achtung</span>}
              <span><i className="ld-kf-amber" /><b>{n.fehlt}</b> Entschuldigung fehlt</span>
              <span><i className="ld-kf-ruhig" /><b>{n.ok}</b> unbedenklich</span>
            </div>
          </div>

          {gruppe('Über der §68-Schwelle', kritischListe, s => zeile(s,
            <span className="ld-kf-detail--rot">{stdText(effFehl(s))} Std. unentschuldigt, Schwelle {_fGrenzen.kritisch}</span>,
            { anteil: effFehl(s) / Math.max(1, _fGrenzen.kritisch), klasse: 'ld-kf-rot' }))}
          {gruppe('Achtung', achtungListe, s => zeile(s,
            <span className="ld-kf-detail--amber">{stdText(effFehl(s))} Std. unentschuldigt, Schwelle {_fGrenzen.kritisch}</span>,
            { anteil: effFehl(s) / Math.max(1, _fGrenzen.kritisch), klasse: 'ld-kf-amber' }))}
          {gruppe('Entschuldigung fehlt', fehltListe, s => zeile(s,
            <span className="ld-kf-detail--amber">{proKind.get(s.id) ? fehltText(proKind.get(s.id)!) : ''}</span>))}

          {okListe.length > 0 && (
            <div className="ld-kl-karte" style={{ marginTop: 16 }}>
              <button type="button" className="ld-kf-ruhig-knopf" aria-expanded={okOffen} onClick={() => setOkOffen(o => !o)}>
                <MIcon name="group" size={20} />
                <span style={{ flex: 1 }}>{okListe.length} {okListe.length === 1 ? 'Kind' : 'Kinder'} unbedenklich</span>
                <MIcon name="expand_more" size={20} style={{ color: 'var(--ld-text-secondary)', transform: okOffen ? 'rotate(180deg)' : 'none', transition: 'transform .15s' }} />
              </button>
              {okOffen && okListe.map(s => (
                <div key={s.id} className="ld-kl-trenn">
                  {zeile(s, s.fehlzeiten.stunden_gesamt > 0
                    ? `${stdText(s.fehlzeiten.stunden_gesamt)} Std. gefehlt, ${stdText(effFehl(s))} unentschuldigt`
                    : 'Keine Fehlzeiten')}
                </div>
              ))}
            </div>
          )}

          {/* Welle J1: ältere Schuljahre — nur lesen */}
          <FehlzeitenVorjahreUebersicht schueler={gefilterteSchueler} />

          {/* §68-PDF als ruhige Zeile */}
          <div className="ld-kl-karte" style={{ marginTop: 16 }}>
            <button type="button" className="ld-kf-pdf" onClick={exportFehlzeiten} disabled={exportLaden || !exportKlasseId}>
              <MIcon name="picture_as_pdf" size={20} />
              <span style={{ flex: 1 }}>
                {exportLaden ? 'PDF wird erstellt …' : 'Fehlzeiten als PDF'}
                <small>§68 HSchG{aktiveKlasseLokal ? `, Klasse ${aktiveKlasseLokal.klasse_name}` : ''}, Schuljahr {schuljahrFenster().label}</small>
              </span>
              <MIcon name="download" size={20} className="ld-kl-pfeil" />
            </button>
          </div>
        </>
      )}

      {scanModal && <FotoScanModal onClose={() => setScanModal(false)} onEintragen={handleEintragen}
        attestpflicht={name => schuelerListe.find(x => x.name === name)?.attestpflicht === 1} />}

      {/* Manuelle Fehlzeit eintragen */}
      {manuellModal && (
        <ManuelleFehlzeitModal
          schuelerListe={gefilterteSchueler}
          streamPromise={fehlzeitFabStreamRef.current}
          onClose={() => { setManuellModal(false); fehlzeitFabStreamRef.current = null; }}
          onEintragen={(name, datum, stunden, tage, entschuldigt, attest) => {
            handleEintragen(name, datum, stunden, tage, entschuldigt, attest);
            setManuellModal(false);
            fehlzeitFabStreamRef.current = null;
          }}
        />
      )}
    </div>
    </div>
  );
}

export function ManuelleFehlzeitModal({
  schuelerListe,
  onClose,
  onEintragen,
  streamPromise,
}: {
  schuelerListe: Schueler[];
  onClose: () => void;
  onEintragen: (name: string, datum: string, stunden: number, tage: number, entschuldigt: boolean, attest?: boolean) => void;
  /** Stream pre-acquired in FAB gesture context (fixes iOS getUserMedia restriction) */
  streamPromise?: Promise<MediaStream> | null;
}) {
  useEscapeKey(onClose);
  const { setIsRecording } = useFab();
  const heute = new Date().toISOString().split('T')[0];
  const [schuelerName, setSchuelerName] = useState(schuelerListe[0]?.name ?? '');
  const [datum, setDatum] = useState(heute);
  const [stunden, setStunden] = useState(6);
  const [tage, setTage] = useState(1);
  const [entschuldigt, setEntschuldigt] = useState(false);
  // Welle B4: Attest gleich mitfragen (nur bei Attestpflicht; Vorgabe „Noch nicht“)
  const [attest, setAttest] = useState(false);
  const mitAttestpflicht = schuelerListe.find(x => x.name === schuelerName)?.attestpflicht === 1;

  // Diktat-State
  const [diktatPhase, setDiktatPhase] = useState<SprechPhase>('idle');
  const [diktatGenutzt, setDiktatGenutzt] = useState(false);
  const [diktatFehler, setDiktatFehler] = useState('');
  const diktatRecorderRef = React.useRef<MediaRecorder | null>(null);
  const diktatChunksRef   = React.useRef<Blob[]>([]);
  // Pre-acquired stream from FAB gesture (iOS Safari: getUserMedia must be called in gesture context)
  const fehlzeitStreamRef = React.useRef<Promise<MediaStream> | null>(streamPromise ?? null);

  // Cleanup bei Unmount (kein Auto-start — Aufnahme erst auf Klick)
  useEffect(() => {
    return () => { diktatRecorderRef.current?.stop(); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startDiktat() {
    if (diktatPhase === 'aufnahme') { diktatRecorderRef.current?.stop(); return; }
    setDiktatFehler('');

    let stream: MediaStream;
    try {
      // Use pre-acquired stream if available (from FAB gesture context on iOS)
      const pending = fehlzeitStreamRef.current;
      fehlzeitStreamRef.current = null;
      stream = pending ? await pending : await mikrofonAnfragen();
    } catch (err) {
      setDiktatFehler(zeigeMikrofonFehler(err, { stattdessenTippen: () => { setDiktatFehler(''); setDiktatPhase('idle'); } }));
      setDiktatPhase('fehler');
      return;
    }

    const kandidaten = ['audio/webm;codecs=opus', 'audio/webm', 'audio/ogg;codecs=opus', 'audio/mp4', 'audio/wav'];
    const mime = kandidaten.find(m => MediaRecorder.isTypeSupported(m)) ?? '';
    const ext  = mime.includes('ogg') ? 'ogg' : mime.includes('mp4') ? 'mp4' : mime.includes('wav') ? 'wav' : 'webm';

    let recorder: MediaRecorder;
    try {
      recorder = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
    } catch (err) {
      setDiktatFehler('MediaRecorder Fehler: ' + (err instanceof Error ? err.message : String(err)));
      setDiktatPhase('fehler');
      stream.getTracks().forEach(t => t.stop());
      return;
    }

    diktatRecorderRef.current = recorder;
    diktatChunksRef.current   = [];
    recorder.ondataavailable  = e => { if (e.data.size > 0) diktatChunksRef.current.push(e.data); };

    recorder.onstop = async () => {
      stream.getTracks().forEach(t => t.stop());
      setIsRecording(false);
      const blob = new Blob(diktatChunksRef.current, { type: recorder.mimeType || mime });
      if (blob.size === 0) {
        setDiktatFehler('Aufnahme leer. Bitte erneut versuchen.');
        setDiktatPhase('fehler');
        return;
      }
      try {
        setDiktatPhase('transkribiert');
        const formData = new FormData();
        formData.append('audio', blob, `aufnahme.${ext}`);
        const jwtToken = holeToken();
        const whisperRes = await fetch(`${API_BASE}/api/transkribiere`, {
          method: 'POST',
          headers: jwtToken ? { Authorization: `Bearer ${jwtToken}` } : {},
          body: formData,
        });
        if (!whisperRes.ok) { const e = await whisperRes.json().catch(() => ({})); throw new Error(e.fehler ?? `Whisper Fehler ${whisperRes.status}`); }
        const { text } = await whisperRes.json();
        if (!text?.trim()) throw new Error('Kein Text erkannt. Bitte deutlicher sprechen.');

        setDiktatPhase('analysiert');
        const result = await apiPost<any>('/api/analysiere-fehlzeit',
          { text, schuelerNamen: schuelerListe.map(s => s.name) });

        // Formular vorausfüllen
        if (result.schueler_name) {
          const match = schuelerListe.find(s => s.name.toLowerCase() === String(result.schueler_name).toLowerCase());
          if (match) setSchuelerName(match.name);
        }
        if (result.datum) setDatum(result.datum);
        if (result.tage    !== undefined && result.tage    !== null) setTage(Number(result.tage));
        // Sanity: bei Mehrtages-Zeitraum Stunden nicht vorbelegen
        if (result.tage && result.tage > 1 && (result.stunden == null || result.stunden === 6)) {
          setStunden(0);
        } else if (result.stunden !== undefined && result.stunden !== null) {
          setStunden(Number(result.stunden));
        }
        setEntschuldigt(!!result.entschuldigt);
        setDiktatGenutzt(true);
        setDiktatPhase('idle');
      } catch (err: unknown) {
        setDiktatFehler(err instanceof Error ? err.message : 'Unbekannter Fehler');
        setDiktatPhase('fehler');
      }
    };

    recorder.start();
    setDiktatPhase('aufnahme');
    setIsRecording(true);
  }

  function speichern() {
    if (!schuelerName) return;
    onEintragen(schuelerName, datum, stunden, tage, entschuldigt || (mitAttestpflicht && attest), mitAttestpflicht && attest);
  }

  const isRecording   = diktatPhase === 'aufnahme';
  const isProcessing  = diktatPhase === 'transkribiert' || diktatPhase === 'analysiert';
  const diktatOk      = diktatPhase === 'idle' && !diktatFehler;

  return (
    <LdSheet open={true} onClose={onClose} maxWidth={520}
      kopf={{ titel: 'Fehlzeit eintragen' }}
      fuss={<LdFensterFuss onAbbrechen={onClose} haupt="Eintragen" onHaupt={speichern} aus={!schuelerName} />}>
      {/* Diktat: eine ruhige Zeile statt großem Kopf */}
      <button type="button" className={`ld-fe-diktat${isRecording ? ' ist-aktiv' : ''}`} onClick={() => void startDiktat()} disabled={isProcessing}
        aria-label={isRecording ? 'Aufnahme stoppen und auswerten' : 'Per Sprache ausfüllen'}>
        <span className="ld-fe-diktat__mic" aria-hidden="true">
          <MIcon name={isRecording ? 'stop' : isProcessing ? 'hourglass_top' : 'mic'} size={20} />
        </span>
        <span>
          <b>{isRecording ? 'Aufnahme läuft' : isProcessing ? (diktatPhase === 'transkribiert' ? 'Wird verschriftet …' : 'Wird ausgewertet …') : 'Per Sprache ausfüllen'}</b>
          <small>{isRecording ? 'Tippen zum Beenden' : 'z. B. „Noah fehlte heute zwei Stunden, entschuldigt“'}</small>
        </span>
      </button>
      {diktatPhase === 'fehler' && (
        <p className="ld-fe-fehler" role="alert">
          {diktatFehler}{' '}
          <button type="button" className="ld-kl-link" onClick={() => { setDiktatFehler(''); setDiktatPhase('idle'); void startDiktat(); }}>Erneut</button>
        </p>
      )}
      {diktatOk && diktatGenutzt && <LdKiHinweis>Aus deinem Diktat ausgefüllt. Bitte kurz prüfen.</LdKiHinweis>}

      <LdFeld label="Schüler/in">
        <LdAuswahl aria-label="Schüler/in" value={schuelerName} onChange={e => setSchuelerName(e.target.value)} placeholder="Schüler/in wählen">
          {schuelerListe.map(s => <option key={s.id} value={s.name}>{s.name}, {s.klasse}</option>)}
        </LdAuswahl>
      </LdFeld>
      <LdFeld label="Datum">
        <LdDatum aria-label="Datum" required value={datum} onChange={e => setDatum(e.target.value)} />
      </LdFeld>
      <LdFeldReihe>
        <LdFeld label="Stunden">
          <LdZahl aria-label="Stunden" value={stunden} min={0} max={12} onChange={setStunden} />
        </LdFeld>
        <LdFeld label="Tage">
          <LdZahl aria-label="Tage" value={tage} min={0} max={30} onChange={setTage} />
        </LdFeld>
      </LdFeldReihe>
      <LdFeld label="Status">
        <LdUmschalter aria-label="Status" wert={entschuldigt ? 'ja' : 'nein'} onChange={w => { setEntschuldigt(w === 'ja'); if (w === 'nein') setAttest(false); }}
          optionen={[{ wert: 'nein', label: 'Unentschuldigt', ton: 'amber' }, { wert: 'ja', label: 'Entschuldigt', ton: 'gruen' }]} />
      </LdFeld>
      {mitAttestpflicht && <AttestFrage attest={attest} onChange={ja => { setAttest(ja); if (ja) setEntschuldigt(true); }} />}
    </LdSheet>
  );
}
